import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import DialogComponent from "../../../../../../common/components/functional/dialogs/DialogComponent";
import {
  DEFAULT_DRAG_HANDLE_SELECTOR,
  NO_DRAG_SELECTOR,
} from "../../../../../../app/constants";
import { useTranslation } from "react-i18next";
import { SelectedTalent } from "../../../../types/editOrder";
import InitialsAvatar from "../../../../../../common/components/functional/initialsAvatar/InitialsAvatar";
import { useNavigate } from "react-router-dom";
import { ChangeEvent, useCallback, useState } from "react";
import { Nullable } from "../../../../../../common/types/nullable";
import { WorkOfferType } from "../helpers";
import { useAppSelector } from "../../../../../../app/hooks";
import {
  selectFailingOverlaps,
  selectPossibleOverlaps,
} from "../../../../../talentProfile/Employment/AssignmentAdditionalInfo/AssignmentsSlice";
import {
  OverlappableAssignment,
  OverlappableEmployment,
} from "../../../../../talents/talentAPI";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { formatDateRange } from "../../../../../../common/helpers/formatDateTime";
import isEmpty from "../../../../../../common/helpers/isEmpty";
import { selectOrderRowById } from "../../../../orderRowsDetailedSlice";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
type Props = {
  selectedTalent: Nullable<SelectedTalent>;
  bundleId: string;
  isOpen: boolean;
  closeDialog: () => void;
  defaultType: WorkOfferType;
};

export const WorkAgreementTypeDialog = ({
  selectedTalent,
  bundleId,
  isOpen,
  closeDialog,
  defaultType,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation("order");
  const order = useAppSelector((state) => selectOrderRowById(state, bundleId));
  const isTargetedOrder = !isEmpty(order?.targetedShiftTargetingRules);

  const [type, setType] = useState<string>(defaultType);
  const [employment, setEmployment] = useState<OverlappableEmployment | null>(
    null,
  );
  // TODO remove continuous employments altogether from these, now dirty hack to get them to work
  const possibleOverlaps = useAppSelector(selectPossibleOverlaps)
    .map((option) => option.employments.filter((e) => !e.failed))
    .flat();
  const failingOverlaps = useAppSelector(selectFailingOverlaps)
    .map((option) =>
      option.employments
        .filter((e) => e.failed)
        .map((e) => ({ ...e, employerName: option.employerName })),
    )
    .flat();

  const disableNewWorkAgreementOption = useCallback(() => {
    if (!isTargetedOrder) {
      return false;
    } else {
      // Check whether overlaps contain employment/assignments that
      // 1) Are also targeted
      // 2) Have same a) eezy company b) TES c) customer company
      const overlaps = possibleOverlaps
        .concat(failingOverlaps)
        .filter((o) => !isEmpty(o.targetedCustomerCompanyIds));
      const hasSameEezyCompany = overlaps.some(
        (o) => o.employerId === order?.eezyCompanyId,
      );
      const hasSameTES = overlaps.some((o) =>
        o.assignments.some((a) => a.details.tesId === order?.taskSpec.tesId),
      );
      const hasSameCustomer = overlaps.some((o) =>
        o.targetedCustomerCompanyIds?.includes(
          // Targeting rules should always include the default rule that contains customer company id
          order?.targetedShiftTargetingRules[0].custCompany?.id,
        ),
      );
      return hasSameTES && hasSameEezyCompany && hasSameCustomer;
    }
  }, [failingOverlaps, isTargetedOrder, order, possibleOverlaps]);

  const disableWorkAgreementCreation = useCallback(() => {
    return (
      (disableNewWorkAgreementOption() && isEmpty(possibleOverlaps)) ||
      (type === WorkOfferType.Overlapping && !employment)
    );
  }, [disableNewWorkAgreementOption, possibleOverlaps, employment, type]);

  const selectType = (event: ChangeEvent<HTMLInputElement>) => {
    const value = (event.target as HTMLInputElement).value;
    setType(value);
  };

  const confirmButtonText =
    type === WorkOfferType.New
      ? t("order:createWorkOffer")
      : t("order:createWorkAssignment");

  const cancel = () => {
    closeDialog();
  };

  const confirm = () => {
    const state = {
      talent: {
        id: selectedTalent!!.id,
        fullName: selectedTalent!!.fullName,
      },
      type: type,
      ...(type === "OVERLAPPING" ? { employment: employment } : {}),
    };
    navigate(`/home/orders/${bundleId}/offer`, {
      state,
    });
  };

  const translatedWorkTitles = useAppSelector(
    (state) => state.localized.workTitle,
  );

  const employmentName = (option: OverlappableEmployment) => {
    const dates = [new Date(option.startDate)];
    if (option.endDate) {
      dates.push(new Date(option.endDate!!));
    }
    return option.details.employerName + ": " + formatDateRange(dates);
  };

  const renderFailure = (failure: string) => {
    const text = t("order:overlapFailDetail:" + failure);
    return " - " + text;
  };

  const renderFailures = (failures: string[]) => {
    return failures.map((f) => renderFailure(f)).join("\n");
  };

  const renderAssgnHead = (overlap: OverlappableAssignment) => {
    const dates = [new Date(overlap.startDate)];
    if (overlap.endDate) {
      dates.push(new Date(overlap.endDate));
    }
    return (
      formatDateRange(dates) +
      " " +
      translatedWorkTitles[overlap.details.workTitleId] +
      " " +
      overlap.details.workLocationName
    );
  };

  const renderAssgn = (overlap: OverlappableAssignment) => {
    return renderAssgnHead(overlap) + ":\n" + renderFailures(overlap.failures);
  };

  const renderEmpHead = (employer: string, overlap: OverlappableEmployment) => {
    const dates = [new Date(overlap.startDate)];
    if (overlap.endDate) {
      dates.push(new Date(overlap.endDate!!));
    }
    const head = formatDateRange(dates) + ":";
    return employer + " " + head;
  };

  const renderEmp = (employer: string, overlap: OverlappableEmployment) => {
    const head = renderEmpHead(employer, overlap);
    if (overlap.failures === null || overlap.failures.length === 0) {
      return (
        <ListItemText
          primary={head}
          secondary={overlap.assignments.map((a) => renderAssgn(a)).join("\n")}
        />
      ); //a => a.details.workLocationName + ": " + translatedWorkTitles[a.details.workTitleId] + ": " + printFailures(a.failures)).join("\n")
    } else {
      return (
        <ListItemText
          primary={head}
          secondary={
            overlap.assignments.map((a) => renderAssgnHead(a)).join("\n") +
            "\n" +
            renderFailures(overlap.failures)
          }
        />
      );
    }
  };

  const renderEmploymentOption = (option: OverlappableEmployment) => {
    return (
      <List sx={{ paddingTop: "0px", paddingBottom: "0px" }}>
        <ListItemText
          primaryTypographyProps={{ fontWeight: 600 }}
          primary={employmentName(option)}
          secondary={t("order:employmentAssignments")}
          secondaryTypographyProps={{
            fontWeight: "600",
            color: "black",
            fontSize: "16px",
            paddingTop: "4px",
          }}
        />
        {option.assignments.map((assignment) => {
          const dates = [new Date(assignment.details.start)];
          if (assignment.details.end) {
            dates.push(new Date(assignment.details.end - 1));
          }

          return (
            <ListItemText
              primary={
                formatDateRange(dates) +
                ": " +
                translatedWorkTitles[assignment.details.workTitleId] +
                ", " +
                assignment.details.workLocationName
              }
            />
          );
        })}
      </List>
    );
  };

  const overlappingControl = () => {
    return (
      <>
        <FormControlLabel
          disabled={possibleOverlaps.length === 0 || isTargetedOrder}
          value={WorkOfferType.Overlapping}
          control={<Radio />}
          label={t("order:createOverLappingWorkAgreement")}
        />
        {isTargetedOrder && (
          <Typography color="error" fontSize={14} pl={4} pb={1}>
            {t("order:cannotCreateOverlapForTargetedOrder")}
          </Typography>
        )}
        {possibleOverlaps.length === 0 && (
          <Typography color={"error"} fontSize={14} pl={4} pb={1}>
            <List disablePadding>
              {failingOverlaps.map((overlap) => (
                <ListItem
                  sx={{ paddingTop: "0px", paddingBottom: "0px" }}
                  key={overlap.employmentId}
                >
                  <ListItemText
                    primary={renderEmp(overlap.employerName, overlap)}
                    secondary={""}
                  />
                </ListItem>
              ))}
            </List>
          </Typography>
        )}
        {type === "OVERLAPPING" && possibleOverlaps.length > 0 && (
          <Autocomplete
            value={employment}
            sx={{
              backgroundColor: "rgba(249, 249, 250, 0.6)",
              paddingLeft: "16px",
              paddingTop: "12px",
              paddingBottom: "12px",
            }}
            options={possibleOverlaps}
            isOptionEqualToValue={(option, value) =>
              option.employmentId === value.employmentId
            }
            getOptionLabel={(option) => employmentName(option)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("order:pickEmployment")}
                size="small"
                name="employments"
                error={false}
              />
            )}
            renderOption={(props, option, { selected }) => {
              const { ...optionProps } = props;
              const key = option.employmentId;
              return (
                <li
                  key={key}
                  {...optionProps}
                  style={{ display: "flex", alignItems: "start" }}
                >
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                    key={key}
                  />
                  {renderEmploymentOption(option)}
                </li>
              );
            }}
            style={{ width: 520 }}
            onChange={(_, newValue: OverlappableEmployment | null) => {
              setEmployment(newValue);
            }}
          />
        )}
      </>
    );
  };

  return (
    <DialogComponent open={isOpen} variant="form" autoWidth>
      <Box sx={{ width: "600px" }}>
        <Typography
          variant="header"
          fontWeight={600}
          fontSize={23}
          sx={{
            cursor: "grab",
            backgroundColor: "#F5F5F5",
            margin: "-16px",
            padding: "16px",
          }}
          className={DEFAULT_DRAG_HANDLE_SELECTOR}
        >
          {t("order:createWorkOfferForTalent")}
        </Typography>
        <Box
          className={NO_DRAG_SELECTOR}
          sx={{
            borderRadius: "8px",
            backgroundColor: "white",
            marginTop: "16px",
            padding: "0px 16px",
          }}
        >
          <Typography pb={1} pt={3}>
            {t("selectedTalent")}:
          </Typography>
          <Box pb={2} display="flex" alignItems="center">
            <InitialsAvatar name={selectedTalent?.fullName || ""} />
            <Typography pl={1} fontSize={20}>
              {selectedTalent?.fullName}
            </Typography>
          </Box>
          <Typography pb={1}>
            {t("talentHasOverlappingAssignmentsInfo")}
          </Typography>
          <Box sx={{ backgroundColor: "#FAFAFB", paddingLeft: "10px" }}>
            {/* TODO add info about the overlapping assignment(s) after YLPE-1921 is done */}
            <RadioGroup value={type} onChange={selectType}>
              <FormControlLabel
                disabled
                value={WorkOfferType.ReplaceAndMerge}
                control={<Radio />}
                label={t("order:replaceAndMergeWorkAgreement")}
              />
              <Typography color={"error"} fontSize={14} pl={4} pb={1}>
                {t("order:optionNotAvailableYet")}
              </Typography>
              {overlappingControl()}
              <FormControlLabel
                disabled={disableNewWorkAgreementOption()}
                value={WorkOfferType.New}
                control={<Radio />}
                label={t("order:createNewWorkAgreement")}
              />
              {disableNewWorkAgreementOption() && (
                <Typography color="error" fontSize={14} pl={4} pb={1}>
                  {t("order:cannotCreateDuplicateTargetedEmployment")}
                </Typography>
              )}
            </RadioGroup>
          </Box>
        </Box>
        <Box pt={4} display="flex" justifyContent="space-between">
          <Button
            variant="outlined"
            sx={{ borderRadius: "20px" }}
            onClick={cancel}
          >
            {t("common:cancel")}
          </Button>

          <Button
            variant="contained"
            sx={{ borderRadius: "20px" }}
            onClick={confirm}
            disabled={disableWorkAgreementCreation()}
          >
            {confirmButtonText}
          </Button>
        </Box>
      </Box>
    </DialogComponent>
  );
};
